import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [team, setTeam] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // API base URL - use relative URL in production, localhost in development
  const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
  
  // Configure axios defaults
  axios.defaults.baseURL = API_URL;
  
  // Add token to requests if available
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  // Handle response errors
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response && error.response.status === 401) {
        // Token expired or invalid
        logout();
        toast.error('Your session has expired. Please log in again.');
      }
      return Promise.reject(error);
    }
  );
  
  // Check if user is authenticated
  const checkAuth = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setTeam(null);
      setLoading(false);
      return;
    }
    
    try {
      // Check if token is expired
      const decoded = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp < currentTime) {
        // Token expired
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
        setTeam(null);
        setLoading(false);
        return;
      }
      
      // Token is valid, get user data
      const response = await axios.get('/api/auth/me');
      
      if (response.data.success) {
        setUser(response.data.user);
        setTeam(response.data.team);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setTeam(null);
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
      setUser(null);
      setTeam(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Login user
  const login = async (email, password, turnstileToken) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password, turnstileToken });
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        setTeam(response.data.team);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'An error occurred during login' 
      };
    }
  };
  
  // Register user and team
  const register = async (userData, turnstileToken) => {
    try {
      const serverData = {
        email: userData.admin.email,
        password: userData.admin.password,
        firstName: userData.admin.firstName,
        lastName: userData.admin.lastName,
        teamName: userData.team.name,
        plan: userData.team.plan,
        turnstileToken
      };

      const response = await axios.post('/api/auth/register', serverData);
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        setTeam(response.data.team);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Log more detailed error information
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);
      }
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'An error occurred during registration' 
      };
    }
  };
  
  // Logout user
  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setTeam(null);
  };
  
  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put('/api/users/me', profileData);
      
      if (response.data.success) {
        setUser(prevUser => ({
          ...prevUser,
          ...response.data.user
        }));
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Update profile error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'An error occurred while updating profile' 
      };
    }
  };
  
  // Request password reset
  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'An error occurred while requesting password reset' 
      };
    }
  };
  
  // Reset password with token
  const resetPassword = async (token, password) => {
    try {
      const response = await axios.post('/api/auth/reset-password', { token, password });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Reset password error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'An error occurred while resetting password' 
      };
    }
  };
  
  // Verify reset token
  const verifyResetToken = async (token) => {
    try {
      const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Verify reset token error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Invalid or expired token' 
      };
    }
  };
  
  // Accept invitation
  const acceptInvitation = async (token, userData) => {
    try {
      const response = await axios.post('/api/users/accept-invitation', {
        token,
        ...userData
      });
      
      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Accept invitation error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'An error occurred while accepting invitation' 
      };
    }
  };
  
  // Verify invitation token
  const verifyInvitation = async (token) => {
    try {
      const response = await axios.get(`/api/invitations/${token}`);
      
      if (response.data.success) {
        return { 
          success: true, 
          data: response.data.invitation 
        };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Verify invitation error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Invalid or expired invitation' 
      };
    }
  };
  
  // Update team info
  const updateTeamInfo = (newTeamInfo) => {
    setTeam(prevTeam => ({
      ...prevTeam,
      ...newTeamInfo
    }));
  };

  // Refresh user data (useful after profile updates)
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/me');
      if (response.data.success) {
        setUser(response.data.user);
        setTeam(response.data.team);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, []);
  
  const value = {
    user,
    team,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    checkAuth,
    verifyResetToken,
    updateProfile,
    forgotPassword,
    resetPassword,
    acceptInvitation,
    verifyInvitation,
    updateTeamInfo,
    refreshUser
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
